import React from "react";
import PropTypes from "prop-types";
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import { currencyChart } from "../utils/currencyChart";

const tickFormat = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumSignificantDigits: 3,
});
const rowLayout = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr) minmax(0, 2fr)",
    sm: "64px minmax(0, 1fr) minmax(120px, 180px)",
  },
  columnGap: { xs: 2, sm: 3 },
};

/** Charts currency totals without converting or combining the reported amounts. */
function CurrencyReport({ data }) {
  const chart = currencyChart(data?.byCurrency || []);

  return (
    <SoftBox
      component="section"
      aria-label="Summary by currency"
      sx={{ px: { xs: 2, sm: 2.875 }, py: 3 }}
    >
      <SoftTypography component="h2" variant="h6">
        Summary by currency
      </SoftTypography>
      {chart.bars.length ? (
        <SoftBox component="figure" aria-label="Total amount by currency" sx={{ m: 0 }}>
          <SoftTypography
            component="figcaption"
            variant="caption"
            color="text"
            sx={{ mt: 0.5, mb: 3 }}
          >
            Amounts in their original currencies. No conversion applied.
          </SoftTypography>
          <SoftBox component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
            {chart.bars.map((item, index) => (
              <SoftBox
                component="li"
                key={item.code + index}
                sx={{ ...rowLayout, alignItems: "center", rowGap: 1, py: { xs: 1.5, sm: 2 } }}
              >
                <SoftTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{ overflowWrap: "anywhere" }}
                >
                  {item.code}
                </SoftTypography>
                <SoftBox
                  aria-hidden="true"
                  sx={({ palette }) => ({
                    position: "relative",
                    height: 28,
                    gridColumn: { xs: "1 / -1", sm: "2" },
                    gridRow: { xs: "2", sm: "1" },
                    borderRadius: "5px",
                    backgroundColor: palette.surface.main,
                  })}
                >
                  {[0, 25, 50, 75, 100].map((position) => (
                    <SoftBox
                      key={position}
                      sx={({ palette }) => ({
                        position: "absolute",
                        top: -12,
                        bottom: -12,
                        left: position + "%",
                        borderLeft: "1px dashed " + palette.grey[300],
                      })}
                    />
                  ))}
                  <SoftBox
                    sx={({ palette }) => ({
                      position: "absolute",
                      top: -4,
                      bottom: -4,
                      left: chart.zero + "%",
                      borderLeft: "1px solid " + palette.grey[400],
                    })}
                  />
                  <SoftBox
                    title={item.code + ": " + item.total}
                    sx={({ palette }) => ({
                      position: "absolute",
                      height: "100%",
                      left: item.left + "%",
                      width: item.width + "%",
                      borderRadius: "5px",
                      backgroundColor: palette.brand.main,
                    })}
                  />
                </SoftBox>
                <SoftTypography
                  variant="button"
                  fontWeight="medium"
                  sx={({ typography }) => ({
                    gridColumn: { xs: "2", sm: "3" },
                    gridRow: 1,
                    textAlign: "right",
                    fontFamily: typography.monoFontFamily,
                    fontVariantNumeric: "tabular-nums",
                    overflowWrap: "anywhere",
                  })}
                >
                  {item.total}
                </SoftTypography>
              </SoftBox>
            ))}
          </SoftBox>
          <SoftBox aria-hidden="true" sx={{ ...rowLayout, mt: 1.5 }}>
            <SoftBox
              sx={{
                gridColumn: { xs: "1 / -1", sm: "2" },
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              {chart.ticks.map((tick, index) => (
                <SoftTypography
                  key={index}
                  variant="caption"
                  color="secondary"
                  sx={{ fontSize: 11 }}
                >
                  {tickFormat.format(tick)}
                </SoftTypography>
              ))}
            </SoftBox>
          </SoftBox>
        </SoftBox>
      ) : (
        <SoftTypography component="p" variant="caption" color="text" sx={{ mt: 1 }}>
          {data?.byCurrency
            ? "No currency totals for these filters."
            : "Run a search to see totals by currency."}
        </SoftTypography>
      )}
    </SoftBox>
  );
}

CurrencyReport.defaultProps = { data: {} };
CurrencyReport.propTypes = { data: PropTypes.object };

export default CurrencyReport;
